<template>
  <form class="demo-form" @submit.prevent="submitDemoRequest">
    <div class="form-intro">
      <div class="form-icon">
        <i class="fas fa-laptop-code"></i>
      </div>
      <p>Let us show you how Eduval can transform your institution. Fill out the form below and our team will reach out to schedule your personalized demo.</p>
    </div>

    <div class="form-fields">
      <div class="input-group" :class="{ 'focus': nameActive, 'has-value': formData.name }">
        <input 
          type="text" 
          id="demo-name" 
          v-model="formData.name" 
          @focus="nameActive = true" 
          @blur="nameActive = false"
          required>
        <label for="demo-name">Your Name</label>
        <div class="input-underline"></div>
      </div>
      
      <div class="input-group" :class="{ 'focus': emailActive, 'has-value': formData.email }">
        <input 
          type="email" 
          id="demo-email" 
          v-model="formData.email" 
          @focus="emailActive = true" 
          @blur="emailActive = false"
          required>
        <label for="demo-email">Your Email</label>
        <div class="input-underline"></div>
      </div>
      
      <div class="input-group" :class="{ 'focus': companyActive, 'has-value': formData.company }">
        <input 
          type="text" 
          id="demo-company" 
          v-model="formData.company" 
          @focus="companyActive = true" 
          @blur="companyActive = false"
          required>
        <label for="demo-company">Institution/Company</label>
        <div class="input-underline"></div>
      </div>
      
      <div class="input-group" :class="{ 'focus': phoneActive, 'has-value': formData.phone }">
        <input 
          type="tel" 
          id="demo-phone" 
          v-model="formData.phone" 
          @focus="phoneActive = true" 
          @blur="phoneActive = false">
        <label for="demo-phone">Phone Number (Optional)</label>
        <div class="input-underline"></div>
      </div>
      
      <div class="input-group" :class="{ 'focus': interestActive, 'has-value': formData.interest }">
        <select 
          id="demo-interest" 
          v-model="formData.interest" 
          @focus="interestActive = true" 
          @blur="interestActive = false"
          required>
          <option value="" disabled selected>Select an option</option>
          <option value="Faculty Information System (FIS)">Faculty Information System (FIS)</option>
          <option value="Integrated Syllabus System (ISS)">Integrated Syllabus System (ISS)</option>
          <option value="Integrated Curriculum System (ICS)">Integrated Curriculum System (ICS)</option>
          <option value="Accreditation and Assessment Management System (AAMS)">Accreditation and Assessment Management System (AAMS)</option>
          <option value="Timetabling Management System (TTMS)">Timetabling Management System (TTMS)</option>
          <option value="Strategic Planning and Performance Management System (SPTS)">Strategic Planning and Performance Management System (SPTS)</option>
          <option value="Multiple Products">Multiple Products</option>
        </select>
        <label for="demo-interest">Product Interest</label>
        <div class="input-underline"></div>
      </div>
      
      <div class="input-group" :class="{ 'focus': messageActive, 'has-value': formData.message }">
        <textarea 
          id="demo-message" 
          v-model="formData.message" 
          @focus="messageActive = true" 
          @blur="messageActive = false"
          required></textarea>
        <label for="demo-message">Tell us about your requirements</label>
        <div class="input-underline"></div>
      </div>
    </div>

    <div class="form-actions">
      <div class="success-message" v-if="submitted">
        <i class="fas fa-check-circle"></i>
        <p>Thank you for your interest! We will contact you shortly to schedule your demo.</p>
      </div>
      <div class="error-message" v-if="error">
        <i class="fas fa-exclamation-circle"></i>
        <p>{{ error }}</p>
      </div>
      <button type="submit" class="submit-button" :disabled="submitting || submitted">
        <span class="button-text">{{ submitting ? 'Sending...' : submitted ? 'Sent!' : 'Request Demo' }}</span>
        <span class="button-icon"><i :class="submitting ? 'fas fa-spinner fa-spin' : submitted ? 'fas fa-check' : 'fas fa-paper-plane'"></i></span>
      </button>
    </div>
  </form>
</template>

<script>
import EmailService from '../services/EmailService';

export default {
  name: 'DemoRequestForm',
  data() {
    return {
      formData: {
        name: '',
        email: '',
        company: '',
        phone: '',
        interest: '',
        message: '',
        subject: 'Demo Request'
      },
      nameActive: false,
      emailActive: false,
      companyActive: false,
      phoneActive: false,
      interestActive: false,
      messageActive: false,
      submitting: false,
      submitted: false,
      error: null
    }
  },
  methods: {
    async submitDemoRequest() {
      this.submitting = true;
      this.error = null;
      
      try {
        // Include the product interest in the message
        const messageWithInterest = `Product Interest: ${this.formData.interest}\n\n${this.formData.message}`;
        const emailData = {
          ...this.formData,
          message: messageWithInterest
        };
        
        const success = await EmailService.sendEmail(emailData);
        
        if (success) {
          this.submitted = true;
          
          // Reset form after successful submission (after a delay)
          setTimeout(() => {
            this.formData = {
              name: '',
              email: '',
              company: '',
              phone: '',
              interest: '',
              message: '',
              subject: 'Demo Request'
            };
          }, 5000);
        } else {
          this.error = 'There was a problem sending your request. Please try again later.';
        }
      } catch (err) {
        this.error = 'An unexpected error occurred. Please try again later.';
        console.error('Demo request error:', err);
      } finally {
        this.submitting = false;
      }
    }
  }
}
</script>

<style scoped>
.demo-form {
  width: 100%;
}

.form-intro {
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: rgba(209, 192, 110, 0.1);
  padding: 1rem;
  border-radius: 8px;
}

.form-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d1c06e, #e0d394);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.form-intro p {
  margin: 0;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
}

.form-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.input-group {
  position: relative;
  margin-bottom: 0.5rem;
}

.input-group:nth-child(5), .input-group:nth-child(6) {
  grid-column: span 2;
}

input, select, textarea {
  width: 100%;
  padding: 0.75rem 0;
  font-size: 1rem;
  border: none;
  outline: none;
  background: transparent;
  color: #333;
  border-bottom: 1px solid #ddd;
  transition: border-color 0.3s;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

select {
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23d1c06e'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0 center;
  background-size: 20px;
  padding-right: 20px;
  cursor: pointer;
}

textarea {
  min-height: 100px;
  resize: vertical;
}

label {
  position: absolute;
  top: 0.75rem;
  left: 0;
  font-size: 1rem;
  color: #999;
  transition: all 0.3s;
  pointer-events: none;
}

.input-underline {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(to right, #d1c06e, #e0d394);
  transition: width 0.3s;
}

.input-group.focus .input-underline {
  width: 100%;
}

.input-group.focus label, .input-group.has-value label {
  top: -0.5rem;
  font-size: 0.8rem;
  color: #d1c06e;
}

/* Special handling for select element when it has a placeholder showing */
.input-group select:invalid {
  color: transparent;
}

.input-group.focus select:invalid,
.input-group.has-value select {
  color: #333;
}

/* Move label up for select even when placeholder is shown */
.input-group select:focus + label, 
.input-group select:valid + label,
.input-group.has-value select + label {
  top: -0.5rem;
  font-size: 0.8rem;
  color: #d1c06e;
}

.form-actions {
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.success-message, .error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  max-width: 70%;
}

.success-message {
  background-color: rgba(75, 181, 67, 0.1);
  color: #4bb543;
}

.error-message {
  background-color: rgba(224, 36, 36, 0.1);
  color: #e02424;
}

.success-message p, .error-message p {
  margin: 0;
  font-size: 0.9rem;
}

.submit-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  background: linear-gradient(135deg, #d1c06e, #e0d394);
  color: white;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(209, 192, 110, 0.3);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 7px 20px rgba(209, 192, 110, 0.4);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.button-icon {
  transition: transform 0.3s;
}

.submit-button:hover:not(:disabled) .button-icon {
  transform: translateX(3px);
}

@media (max-width: 768px) {
  .form-fields {
    grid-template-columns: 1fr;
  }
  
  .input-group:nth-child(5), .input-group:nth-child(6) {
    grid-column: span 1;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 1rem;
  }
  
  .success-message, .error-message {
    max-width: 100%;
  }
}
</style> 